// src/features/executive/components/DrillOutlet/DrillOutlet.tsx
// Reads the top of executive.drillStack and renders the matching drill
// body. Unknown ids render a localised "Coming soon" fallback.

import { Typography } from '@mui/material'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import type { ReactNode } from 'react'
import type { ExecutiveState } from 'app/slice/executive.slice'
import RevenueDrill from 'features/executive/performance/drill/RevenueDrill'
import KeyProceduresDrill from 'features/executive/performance/drill/KeyProceduresDrill'
import AppointmentsDrill from 'features/executive/performance/drill/AppointmentsDrill'
import TopProceduresDrill from 'features/executive/sales/drill/TopProceduresDrill'
import TopDoctorsDrill from 'features/executive/sales/drill/TopDoctorsDrill'
import TopProductsDrill from 'features/executive/sales/drill/TopProductsDrill'
import CategoryMixDrill from 'features/executive/sales/drill/CategoryMixDrill'
import NewPatientTrendDrill from 'features/executive/insight/drill/NewPatientTrendDrill'

// Drill body registry — one entry per drillable widget.
const REGISTRY: Record<string, () => ReactNode> = {
  revenue: () => <RevenueDrill />,
  'key-procedures': () => <KeyProceduresDrill />,
  appointments: () => <AppointmentsDrill />,
  'top-procedures': () => <TopProceduresDrill />,
  'top-doctors': () => <TopDoctorsDrill />,
  'top-products': () => <TopProductsDrill />,
  'category-mix': () => <CategoryMixDrill />,
  'new-patient-trend': () => <NewPatientTrendDrill />,
}

export default function DrillOutlet() {
  const { t } = useTranslation()
  const stack = useSelector((s: { executive: ExecutiveState }) => s.executive.drillStack)
  const top = stack[stack.length - 1]

  if (!top) return null

  const render = REGISTRY[top.id]
  if (render) return <>{render()}</>

  return (
    <Typography variant="body2" color="text.secondary">
      {t('EXECUTIVE.DRILL.COMING_SOON', 'Drill-down body for this widget is not wired yet.')}
    </Typography>
  )
}
